/**
 * KB Resolved-Story API endpoints and types (Wellington #5, read-only).
 *
 * Consumes the ADMIN-gated "resolved story" endpoint, which walks the product
 * hierarchy and resolves each question's answer with inheritance provenance —
 * showing, per question, whether each snippet is the node's own content or
 * inherited from an ancestor (and from which depth).
 */

import apiClient from './client';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

/** Provenance / resolution status. Matches the backend literal set. */
export type ResolvedStatus = 'own' | 'inherited' | 'gap' | 'not_applicable';

/**
 * A single resolved snippet — a content block placed into a question's answer,
 * carrying where it came from (own node vs. inherited ancestor). Matches the
 * backend `ResolvedSnippetOut`.
 */
export interface ResolvedSnippetOut {
  block_key: string;
  content_block_id: string | null;
  body_text: string | null;
  body_html?: string | null;
  placement_role: string;
  provenance: ResolvedStatus;
  source_node_id: number;
  source_depth: number;
  /** The placement row id — present when the snippet can be un-placed (its own node). */
  scope_id: string | null;
}

/**
 * A resolved question with its assembled snippets and inheritance metadata.
 * Matches the backend `ResolvedQuestionOut`.
 */
export interface ResolvedQuestionOut {
  canonical_question_id: number;
  question_text: string;
  composition_mode: string;
  inheritable: boolean;
  status: ResolvedStatus;
  is_gap: boolean;
  snippets: ResolvedSnippetOut[];
  missing_block_keys: string[];
  notes: string[];
}

/** A category grouping of resolved questions under a node. Matches `ResolvedCategory`. */
export interface ResolvedCategory {
  category_node_id: number | null;
  display_name: string;
  questions: ResolvedQuestionOut[];
}

/**
 * Structural per-category status counts at a spine node (the skeleton read).
 * Matches the backend `CategoryCount`. The dots are indicative (own/inherited);
 * exact per-question status + bodies come from the node-detail read.
 */
export interface CategoryCount {
  category_node_id: number | null;
  display_name: string;
  own: number;
  inherited: number;
  gap: number;
  not_applicable: number;
}

/**
 * A node in the product hierarchy with its per-category status COUNTS (skeleton).
 * The full node list is returned FLAT (see `ResolvedStoryResponse.nodes`); rebuild
 * the tree via `parent_id`. Matches the backend `ResolvedNode`. Question bodies are
 * NOT here — they load per (node, category) via `getResolvedNodeAnswers`.
 */
export interface ResolvedNode {
  node_id: number;
  parent_id: number | null;
  node_kind: string;
  display_name: string;
  depth: number;
  category_counts: CategoryCount[];
  /**
   * For `node_kind === 'level_header'`: a canonical level shown as a real, authorable
   * header. `not_modeled` = the firm hasn't broken this level into instances yet, so it
   * carries the enrichment nudge (how many vault rows are ready + the blurb).
   */
  not_modeled: boolean;
  not_applicable: boolean;
  enrichable_count: number | null;
  enrichable_total: number | null;
  blurb: string | null;
}

/**
 * The resolved story payload. `nodes` is FLAT, ordered by depth then node_id.
 * Matches the backend `ResolvedStoryResponse`.
 */
export interface ResolvedStoryResponse {
  firm_id: number | null;
  root_node_id: number;
  nodes: ResolvedNode[];
}

// -----------------------------------------------------------------------------
// API Endpoints
// -----------------------------------------------------------------------------

const KB_BASE = '/v1/insight/admin/kb';

/**
 * Get the resolved story for a product hierarchy.
 *
 * When `rootNodeId` is provided, resolves the subtree rooted at that node
 * (`GET ${KB_BASE}/resolved-stories/{rootNodeId}`); otherwise resolves from the
 * firm's product root (`GET ${KB_BASE}/resolved-stories`).
 */
export async function getResolvedStory(
  rootNodeId?: number
): Promise<ResolvedStoryResponse> {
  const url =
    rootNodeId !== undefined
      ? `${KB_BASE}/resolved-stories/${rootNodeId}`
      : `${KB_BASE}/resolved-stories`;
  const response = await apiClient.get<ResolvedStoryResponse>(url);
  return response.data;
}

/**
 * Get the exact resolved answers for ONE (node, category) — the bounded detail read.
 * Always pass `categoryNodeId` (the all-categories form resolves every question for the
 * node and is intentionally avoided). `null` selects the "Uncategorized" group.
 * `GET ${KB_BASE}/resolved-stories/{nodeId}/answers?category_node_id=...`.
 */
export async function getResolvedNodeAnswers(
  nodeId: number,
  categoryNodeId: number | null
): Promise<ResolvedCategory> {
  const response = await apiClient.get<ResolvedCategory>(
    `${KB_BASE}/resolved-stories/${nodeId}/answers`,
    { params: categoryNodeId !== null ? { category_node_id: categoryNodeId } : undefined }
  );
  return response.data;
}

/** The new current version returned by a copy-on-write snippet edit. */
export interface SnippetUpdated {
  block_key: string;
  content_block_id: string;
  version: number;
  body_text: string;
  body_html?: string | null;
}

/**
 * Edit a snippet's body (copy-on-write). Because every placement references the
 * snippet's logical `block_key`, this single edit re-resolves everywhere the
 * snippet is inherited — "edit once, applies everywhere".
 * `PATCH ${KB_BASE}/snippets/{block_key}`.
 */
export async function editSnippet(
  blockKey: string,
  bodyText: string,
  expectedVersion?: number,
  bodyHtml?: string
): Promise<SnippetUpdated> {
  const response = await apiClient.patch<SnippetUpdated>(
    `${KB_BASE}/snippets/${encodeURIComponent(blockKey)}`,
    {
      body_text: bodyText,
      expected_version: expectedVersion ?? null,
      body_html: bodyHtml ?? null,
    }
  );
  return response.data;
}

/** Mark a canonical level-header not-applicable for this firm (or clear it). */
export async function setHeaderNotApplicable(
  nodeId: number,
  notApplicable: boolean
): Promise<void> {
  await apiClient.patch(`${KB_BASE}/headers/${nodeId}/not-applicable`, {
    not_applicable: notApplicable,
  });
}

/** Model a Region header: nest regionally-named products under inferred regions. */
export interface RegionPromoted {
  node_id: number;
  regions: number;
  regional: number;
  loose: number;
}

export async function promoteRegionHeader(nodeId: number): Promise<RegionPromoted> {
  const response = await apiClient.post<RegionPromoted>(`${KB_BASE}/headers/${nodeId}/promote`);
  return response.data;
}

// -----------------------------------------------------------------------------
// Authoring (Wellington #2 Phase-1): write a block once + place it to inherit.
// Matches the backend kb_content_authoring endpoints (firm-scoped, ADMIN-gated).
// -----------------------------------------------------------------------------

export type ContentKind = 'narrative' | 'qa_answer' | 'table_note';
export type ContentStatus = 'draft' | 'approved' | 'stale' | 'retired';

/** The created content block. Matches the backend `ContentBlockCreated`. */
export interface ContentBlockCreated {
  content_block_id: string;
  block_key: string;
  firm_id: number | null;
  title: string | null;
  content_kind: string;
  status: string;
  version: number;
}

export interface CreateContentBlockInput {
  block_key: string;
  title: string;
  content_kind: ContentKind;
  body_text: string;
  status?: ContentStatus;
  folder_path?: string | null;
  /** SuperAdmin may target a firm; a firm admin is forced to their own. */
  firm_id?: number | null;
}

/** Create a firm-scoped content block. `POST ${KB_BASE}/content-blocks`. */
export async function createContentBlock(
  input: CreateContentBlockInput
): Promise<ContentBlockCreated> {
  const response = await apiClient.post<ContentBlockCreated>(`${KB_BASE}/content-blocks`, input);
  return response.data;
}

/** The created placement. Matches the backend `ScopePlacementCreated`. */
export interface ScopePlacementCreated {
  scope_id: string;
  block_key: string;
  firm_id: number | null;
  canonical_question_id: number;
  spine_node_id: number;
  flows_to_descendants: boolean;
  precedence: number;
}

export interface PlaceScopeInput {
  block_key: string;
  canonical_question_id: number;
  spine_node_id: number;
  flows_to_descendants?: boolean;
  precedence?: number;
}

/**
 * Place an approved block on a product-spine node so the resolver inherits it to
 * descendants — the "write once, applies everywhere" placement. `POST ${KB_BASE}/scopes`.
 */
export async function placeScope(input: PlaceScopeInput): Promise<ScopePlacementCreated> {
  const response = await apiClient.post<ScopePlacementCreated>(`${KB_BASE}/scopes`, input);
  return response.data;
}

/** A row in the firm's content-block library. Matches the backend `ContentBlockSummary`. */
export interface ContentBlockSummary {
  content_block_id: string;
  block_key: string;
  title: string | null;
  content_kind: string;
  status: string;
  version: number;
}

export interface ContentBlockList {
  firm_id: number | null;
  blocks: ContentBlockSummary[];
}

/** List the firm's current content blocks (for picking one to place). `GET ${KB_BASE}/content-blocks`. */
export async function listContentBlocks(firmId?: number): Promise<ContentBlockList> {
  const response = await apiClient.get<ContentBlockList>(`${KB_BASE}/content-blocks`, {
    params: firmId !== undefined ? { firm_id: firmId } : undefined,
  });
  return response.data;
}

/**
 * Transition a block's lifecycle status (e.g. draft → approved before placing).
 * `PATCH ${KB_BASE}/content-blocks/{block_key}/status`.
 */
export async function setContentBlockStatus(
  blockKey: string,
  status: ContentStatus
): Promise<ContentBlockCreated> {
  const response = await apiClient.patch<ContentBlockCreated>(
    `${KB_BASE}/content-blocks/${encodeURIComponent(blockKey)}/status`,
    { status }
  );
  return response.data;
}

/** Un-place a snippet: soft-delete a placement. `DELETE ${KB_BASE}/scopes/{scope_id}`. */
export async function deleteScope(scopeId: string): Promise<void> {
  await apiClient.delete(`${KB_BASE}/scopes/${encodeURIComponent(scopeId)}`);
}
